import GoogleCloudStorageUpload from './google-cloud-storage-upload';

import i18n, { t } from './i18n';

if (window.fileUploaderTranslations) {
  i18n.addTranslations(window.fileUploaderTranslations);
}

const icons = {
  loading: `
    <svg class="icon-svg icon-loader">
      <use xlink:href="#icon-loader"></use>
    </svg>
  `,
  success: `
    <svg class="icon-svg icon-check">
      <use xlink:href="#icon-check"></use>
    </svg>
  `,
};

/**
 * Component which converts basic file input field to more advanced input that supports resumable uploads.
 */
class FileUploader {

  /**
   * @type {GoogleCloudStorageUpload[]}
   */
  #fileUploads = [];

  #maxFileCount = 1;
  #elements = {
    /**
     * @type {HTMLInputElement}
     */
    input: null,
    /**
     * @type {HTMLDivElement}
     */
    fileContainer: null,
    /**
     * @type {HTMLDivElement}
     */
    dragArea: null,
  };

  /**
   * Create FileUploader instance.
   *
   * @param {HTMLInputElement} fileInput File input to convert to file uploader component.
   */
  constructor(fileInput) {
    this.#maxFileCount = fileInput.multiple ? parseInt(fileInput.dataset.maxFiles || '5') : 1;
    const id = fileInput.id || fileInput.name;

    fileInput.insertAdjacentHTML('afterend', `
      <input class="js-file-uploader-hidden-input" data-id="${id}" type="hidden" name="${fileInput.name}" value="" />
      <div class="file-uploader">
        <div class="file-uploader__files js-file-uploader-files" data-id="${id}"></div>
        <div class="file-uploader__dragarea js-file-uploader-dragarea" data-id="${id}">
          <span>${t('file_uploader__input__draganddrop')}</span>
          <span>${t('file_uploader__input__or')}</span>
          <span>${t('file_uploader__input__click')}</span>
        </div>
      </div>
    `);

    fileInput.multiple = false;
    fileInput.dataset.originalName = fileInput.name;
    fileInput.name = '';

    this.#elements.input = fileInput;
    this.#elements.hiddenInput = document.querySelector(`.js-file-uploader-hidden-input[data-id="${id}"]`);
    this.#elements.fileContainer = document.querySelector(`.js-file-uploader-files[data-id="${id}"]`);
    this.#elements.dragArea = document.querySelector(`.js-file-uploader-dragarea[data-id="${id}"]`);
    this.#elements.dragArea.appendChild(fileInput);
    fileInput.addEventListener('dragenter', (e) => {
      //console.log('dragover', { e });
      e.target.classList.add('dragover');
    });
    fileInput.addEventListener('dragleave', (e) => {
      //console.log('dragleave', { e });
      e.target.classList.remove('dragover');
    });
    fileInput.addEventListener('dragend', (e) => {
      //console.log('dragend', { e });
      e.target.classList.remove('dragover');
    });
    fileInput.addEventListener('drop', (e) => {
      //console.log('drop', { e });
      e.target.classList.remove('dragover');
    });

    fileInput.addEventListener('change', (e) => {
      const newFiles = Array.from(fileInput.files).filter(file => {
        const oldFile = this.#fileUploads.find(upload => upload.getFile()?.name === file.name && upload.getFile()?.size === file.size);
        return !oldFile;
      });
      newFiles.forEach(file => {
        const uploadInstance = new GoogleCloudStorageUpload();
        const uploadId = uploadInstance.getId();
        const html = this.#renderFileTemplate(uploadId, file);
        this.#elements.fileContainer.insertAdjacentHTML('beforeend', html);
        const fileEl = this.#elements.fileContainer.querySelector(`.js-upload-file[data-upload="${uploadId}"]`);
        const fileStatus = fileEl.querySelector('.js-upload-file-status');
        const fileButtons = fileEl.querySelectorAll('.js-upload-file-button');

        const updateHiddenInput = () => {
          const completedUploads = this.#fileUploads.filter(item => item.getUploadResult()?.status === 'completed');
          const fileUrls = completedUploads.map(item => item.getUploadResult().resultUrl || item.getUploadResult().id);
          this.#elements.hiddenInput.value = fileUrls.join(',');
        };

        const updateFileCount = () => {
          const fileLimitReached = this.#isMaxFiles();
          this.#elements.dragArea.hidden = fileLimitReached;
          this.#elements.input.disabled = fileLimitReached;
        };

        const removeFile = () => {
          fileEl.remove();
          this.#fileUploads = this.#fileUploads.filter(item => item !== uploadInstance);
        };

        function updateFileView(status = 'create', progress = 0, message = '') {
          let visibleButtons = [];
          let icon = '';
          let statusText = '';
          const formattedProgress = progress > 0 || status === 'uploading' ? ` - ${Math.floor(progress * 100)}%` : '';
          if (status === 'new') {
            statusText = t('file_uploader__status__new');
            icon = icons.loading;
            visibleButtons = ['continue', 'cancel'];
          }
          else if (status === 'uploading') {
            statusText = t('file_uploader__status__uploading') + formattedProgress;
            icon = icons.loading;
            visibleButtons = ['pause', 'cancel'];
          }
          else if (status === 'paused') {
            statusText = t('file_uploader__status__paused') + formattedProgress;
            visibleButtons = ['continue', 'cancel'];
          }
          else if (status === 'completed') {
            statusText = t('file_uploader__status__completed');
            icon = icons.success;
            visibleButtons = ['remove'];
          }
          else if (status === 'interrupted') {
            statusText = t('file_uploader__status__interrupted') + formattedProgress;
            visibleButtons = ['continue', 'cancel'];
          }
          else if (status === 'failed') {
            statusText = t('file_uploader__status__failed');
            visibleButtons = ['remove'];
          }
          else if (status === 'error') {
            statusText = t('file_uploader__error__upload_start') + (message ? ': ' + message : '');
            visibleButtons = ['remove'];
          }

          fileEl.dataset.status = status;
          fileStatus.innerHTML = icon + statusText;
          fileButtons.forEach(btn => {
            btn.hidden = !visibleButtons.includes(btn.dataset.action);
          });
        }

        fileButtons.forEach(el => {
          el.addEventListener('click', (e) => {
            if (e.target.dataset.action === 'cancel') {
              if (uploadInstance.cancelUpload()) {
                e.target.disabled = true;
              }
            }
            else if (e.target.dataset.action === 'pause') {
              if (uploadInstance.pauseUpload()) {
                e.target.disabled = true;
              }
            }
            else if (e.target.dataset.action === 'continue') {
              if (uploadInstance.upload()) {
                e.target.disabled = true;
              }
            }
            else if (e.target.dataset.action === 'remove') {
              removeFile();
              updateFileCount();
              updateHiddenInput();
            }
          });
        });

        uploadInstance.addEventListener('status', (status) => {
          if (status === 'canceled') {
            removeFile();
          }

          updateFileView(status, uploadInstance.getUploadResult().progress);
          updateFileCount();
          updateHiddenInput();

          this.#toggleFormSubmit();
          fileButtons.forEach(btn => {
            btn.disabled = false;
          });
        });
        uploadInstance.addEventListener('progress', (progress) => {
          updateFileView(uploadInstance.getUploadResult().status, progress);
        });

        uploadInstance.createUpload(file, {
          context: fileInput.dataset.uploadContext,
          startUpload: true
        }).then(() => {
          uploadInstance.error = false;
        }).catch((e) => {
          uploadInstance.error = e.message;
          updateFileView('error', 0, e.message);
          updateFileCount();
        });

        this.#fileUploads.push(uploadInstance);
        updateFileCount();
      });

      this.#toggleFormSubmit();
      fileInput.value = '';
    });
  }

  #toggleFormSubmit() {
    const form = this.#elements.input?.form;
    if (!form) {
      return;
    }

    const hasActiveUploads = this.#fileUploads.some(item => {
      const status = item.getUploadResult()?.status;
      return !status || ['new', 'uploading', 'paused', 'stopped'].includes(status);
    });
    form.querySelectorAll('[type="submit"]').forEach(btn => {
      btn.disabled = hasActiveUploads;
    });
  }

  #isMaxFiles() {
    const validUploads = this.#fileUploads.filter(upload => {
      const status = upload.getUploadResult()?.status;
      return (!status && !upload.error) || ['new', 'uploading', 'paused', 'completed'].includes(status);
    });
    return validUploads.length >= this.#maxFileCount;
  }

  /**
   * Get maximum amount of files that can be uploaded.
   *
   * @returns {number}
   */
  getFileLimit() {
    return this.#maxFileCount;
  }

  /**
   * Get wrapped file input.
   *
   * @returns {HTMLInputElement}
   */
  getInput() {
    return this.#elements.input;
  }

  /**
   * Get file upload instances.
   *
   * @returns {GoogleCloudStorageUpload[]}
   */
  getFileUploads() {
    return this.#fileUploads;
  }

  /**
   * Get rendered template for single upload.
   *
   * @param {string} id Upload id.
   * @param {File} file File to be uploaded.
   * @returns {string} Rendered html.
   */
  #renderFileTemplate(id, file) {
    let filesize = file.size;

    if (file.size > 1024 * 1024 * 1024) {
      filesize = (file.size / (1024 * 1024 * 1024)).toFixed(1) + 'GB';
    }
    else if (file.size > 1024 * 1024) {
      filesize = (file.size / (1024 * 1024)).toFixed(1) + 'MB';
    }
    else {
      filesize = (file.size / 1024).toFixed(3) + 'KB';
    }

    const name = file.name;
    return `
      <div class="file-uploader-file js-upload-file" data-upload="${id}" data-status="new">
        <div class="file-uploader-file__content">
          <div class="file-uploader-file__name">
            <span class="file-uploader-file__filename">${name}</span>
            <span class="file-uploader-file__filesize">(${filesize})</span>
          </div>
          <div class="file-uploader-file__status js-upload-file-status">
            <svg class="icon-svg icon-loader">
              <use xlink:href="#icon-loader"></use>
            </svg>
            ${t('file_uploader__status__new')}
          </div>
        </div>

        <div class="file-uploader-file__actions">
          <button type="button" class="file-uploader-file__button js-upload-file-button" data-action="pause" aria-label="${t('file_uploader__file__pause')}" title="${t('file_uploader__file__pause')}" hidden>
            <svg class="icon-svg icon-pause-border">
              <use xlink:href="#icon-pause-border"></use>
            </svg>
          </button>
          <button type="button" class="file-uploader-file__button js-upload-file-button" data-action="continue" aria-label="${t('file_uploader__file__continue')}" title="${t('file_uploader__file__continue')}" hidden>
            <svg class="icon-svg icon-play-border">
              <use xlink:href="#icon-play-border"></use>
            </svg>
          </button>
          <button type="button" class="file-uploader-file__button js-upload-file-button" data-action="remove" aria-label="${t('file_uploader__file__remove')}" title="${t('file_uploader__file__remove')}" hidden>
            <svg class="icon-svg icon-close">
              <use xlink:href="#icon-close"></use>
            </svg>
          </button>
          <button type="button" class="file-uploader-file__button js-upload-file-button" data-action="cancel" aria-label="${t('file_uploader__file__cancel')}" title="${t('file_uploader__file__cancel')}" hidden>
            <svg class="icon-svg icon-close">
              <use xlink:href="#icon-close"></use>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

}

export default FileUploader;
